import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Determinate progress bar. Stays in the DOM at 0% so CSS transitions
 * look smoother on later updates.
 *
 * When `percent` is `null` / `undefined`, the bar renders an
 * indeterminate animation (semi-opaque sweep) which honors
 * prefers-reduced-motion via the global rule in index.css.
 *
 * `tone` defaults to `primary`; use `success` once a flow completes if
 * you want the bar to swap colors as confirmation.
 */
interface ProgressProps extends React.ComponentProps<'div'> {
  percent?: number | null
  tone?: 'primary' | 'success' | 'warning' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  label?: string
}

const toneClass: Record<NonNullable<ProgressProps['tone']>, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
}

const sizeClass: Record<NonNullable<ProgressProps['size']>, string> = {
  sm: 'h-1',
  md: 'h-1.5',
  lg: 'h-2',
}

function Progress({
  percent,
  tone = 'primary',
  size = 'md',
  label,
  className,
  ...props
}: ProgressProps) {
  const determinate = typeof percent === 'number'
  const value = determinate ? Math.max(0, Math.min(100, percent!)) : undefined

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={determinate ? value : undefined}
      aria-label={label}
      data-slot="progress"
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-muted',
        sizeClass[size],
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500 ease-out',
          toneClass[tone],
          !determinate && 'cockpit-progress-indeterminate',
        )}
        style={{
          width: determinate ? `${value}%` : '40%',
        }}
      />
    </div>
  )
}

export { Progress }
